/**
 * Parse docs/placeholder-vocabulary.md into the runtime vocabulary.
 * The markdown file is the source of truth. Do not invent tokens here.
 */

export const STATUS = {
  ready: "ready",
  transform: "transform",
  reserved: "reserved",
};

export const STATUS_LABELS = {
  ready: "Ready to use",
  transform: "Exist but need transformation",
  reserved: "Reserved / do not exist yet",
};

export const STATUS_FILTERS = [
  { id: "all", label: "Status: All" },
  { id: STATUS.ready, label: "Status: Ready to use" },
  { id: STATUS.transform, label: "Status: Exist but need transformation" },
  { id: STATUS.reserved, label: "Status: Reserved / do not exist yet" },
];

const TOKEN_RE = /@[a-z][a-z0-9]*@/g;

function stripCell(value) {
  return String(value || "")
    .replace(/`/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function splitRow(line) {
  if (!line.startsWith("|")) return null;
  const cells = line.split("|").slice(1, -1).map((cell) => cell.trim());
  if (!cells.length) return null;
  if (cells.every((cell) => /^[-:]+$/.test(cell))) return null;
  return cells;
}

function statusFromReadiness(readiness) {
  const text = stripCell(readiness);
  if (/^ready\b/i.test(text)) return STATUS.ready;
  if (/^transform/i.test(text)) return STATUS.transform;
  return null;
}

function parseMasterList(markdown) {
  const start = markdown.indexOf("## Master list");
  const end = markdown.indexOf("## Recommended PDF Header");
  if (start < 0 || end < 0 || end <= start) {
    throw new Error("placeholder vocabulary markdown is missing the Master list section");
  }
  const body = markdown.slice(start, end);
  const items = [];
  let section = "";
  let headers = null;

  for (const line of body.split("\n")) {
    if (line.startsWith("### ")) {
      section = line.replace(/^###\s+/, "").trim();
      headers = null;
      continue;
    }
    const cells = splitRow(line);
    if (!cells) continue;
    if (cells[0] === "Placeholder") {
      headers = cells;
      continue;
    }
    if (!headers || headers[3] !== "Readiness") continue;

    const placeholder = stripCell(cells[0]);
    const status = statusFromReadiness(cells[3]);
    if (!/^@[a-z][a-z0-9]*@$/.test(placeholder) || !status) continue;
    items.push({
      placeholder,
      meaning: stripCell(cells[1]),
      source: stripCell(cells[2]),
      status,
      kind: "master",
      section,
    });
  }
  return items;
}

function parseReservedList(markdown) {
  const start = markdown.indexOf("### 3. Do not exist today");
  const end = markdown.indexOf("## Resolver notes");
  if (start < 0 || end < 0 || end <= start) {
    throw new Error("placeholder vocabulary markdown is missing the reserved-token section");
  }
  const body = markdown.slice(start, end);
  const items = [];
  let headers = null;

  for (const line of body.split("\n")) {
    const cells = splitRow(line);
    if (!cells) continue;
    if (cells[0] === "Reserved token") {
      headers = cells;
      continue;
    }
    if (!headers) continue;
    const tokens = cells[0].match(TOKEN_RE) || [];
    const meaning = stripCell(cells[1]);
    const reason = stripCell(cells[2] || "");
    for (const placeholder of tokens) {
      items.push({
        placeholder,
        meaning,
        source: reason || "Reserved / not implemented",
        status: STATUS.reserved,
        kind: "reserved",
        section: "Reserved / do not exist yet",
      });
    }
  }
  return items;
}

export function parsePlaceholderVocabularyMd(markdown) {
  const master = parseMasterList(markdown);
  const reserved = parseReservedList(markdown);
  const seen = new Set();
  const items = [];
  for (const item of [...master, ...reserved]) {
    if (seen.has(item.placeholder)) continue;
    seen.add(item.placeholder);
    items.push(item);
  }
  return items;
}

export function filterPlaceholderVocabulary(
  items,
  { placeholderQuery = "", meaningQuery = "", status = "all" } = {},
) {
  const placeholderNeedle = String(placeholderQuery || "").trim().toLowerCase();
  const meaningNeedle = String(meaningQuery || "").trim().toLowerCase();
  return (items || []).filter((item) => {
    if (status && status !== "all" && item.status !== status) return false;
    if (placeholderNeedle && !item.placeholder.toLowerCase().includes(placeholderNeedle)) {
      return false;
    }
    if (meaningNeedle && !item.meaning.toLowerCase().includes(meaningNeedle)) {
      return false;
    }
    return true;
  });
}

export function masterItems(items) {
  return (items || []).filter((item) => item.kind === "master");
}
